import { OwnershipGraph, OwnershipNode, DocumentRecord } from './types';
import { getEdgeTypeLabel, formatFraction } from './data-helpers';
import { formatNumber } from '../../../utils/number';

export interface ConsolidationSource {
  entityName: string;
  entityId: number;
  fraction: string;
  documentId: number | null;
  documentTitle: string | null;
  eventType: string;
  children: LineageStep[];
}

export interface LineageStep {
  fromEntityName: string;
  fromEntityId: number;
  fromFraction: string;
  multiplier: string;
  toEntityName: string;
  toFraction: string;
  documentId: number | null;
  documentTitle: string | null;
  eventType: string;
  children: LineageStep[];
  isConsolidation?: boolean;
  consolidationSources?: ConsolidationSource[];
  isEstablishmentRoot?: boolean;
  depthCapped?: boolean;
}

function getFirstNonZeroInterest(node: OwnershipNode) {
  return (
    node.interests.find((i) => i.fractionF > 0) ?? node.interests[0] ?? null
  );
}

function findDepthCappedRoot(
  startNodeId: string,
  graph: OwnershipGraph
): LineageStep[] {
  const visited = new Set<string>();
  let currentId = startNodeId;
  const maxHops = Object.keys(graph.nodes).length + 1;

  for (let hop = 0; hop < maxHops; hop++) {
    if (visited.has(currentId)) break;
    visited.add(currentId);

    const incomingEdgeIds = graph.reverseAdjacency[currentId] ?? [];
    if (incomingEdgeIds.length === 0) {
      const rootNode = graph.nodes[currentId];
      if (!rootNode) return [];

      const interest = getFirstNonZeroInterest(rootNode);
      const fraction = interest
        ? formatFraction(interest.fractionF)
        : '0.00000000';

      return [
        {
          fromEntityName: rootNode.entity.entityName,
          fromEntityId: rootNode.entity.entityId,
          fromFraction: fraction,
          multiplier: '1.00000000',
          toEntityName: rootNode.entity.entityName,
          toFraction: fraction,
          documentId: null,
          documentTitle: null,
          eventType: '',
          children: [],
          isEstablishmentRoot: true,
          depthCapped: true,
        },
      ];
    }

    const firstEdgeId = incomingEdgeIds[0];
    const edge = graph.edges[firstEdgeId];
    if (!edge || edge.fromNodeId === null) break;
    currentId = String(edge.fromNodeId);
  }

  return [];
}

function buildLineageSteps(
  nodeId: string,
  graph: OwnershipGraph,
  documents: Record<string, DocumentRecord>,
  visited: Set<string>,
  depth: number,
  maxDepth: number
): LineageStep[] {
  if (depth >= maxDepth) {
    return findDepthCappedRoot(nodeId, graph);
  }

  const incomingEdgeIds = graph.reverseAdjacency[nodeId] ?? [];
  if (incomingEdgeIds.length === 0) return [];

  const isConsolidation = incomingEdgeIds.length > 1;

  if (isConsolidation) {
    const toNode = graph.nodes[nodeId];
    if (!toNode) return [];

    const toInterest = getFirstNonZeroInterest(toNode);
    const toFraction = toInterest
      ? formatFraction(toInterest.fractionF)
      : '0.00000000';

    const consolidationSources: ConsolidationSource[] = [];

    for (const edgeId of incomingEdgeIds) {
      const edge = graph.edges[edgeId];
      if (!edge) continue;

      const fromNodeId =
        edge.fromNodeId !== null ? String(edge.fromNodeId) : null;
      const fromNode = fromNodeId ? graph.nodes[fromNodeId] : null;
      if (!fromNode) continue;

      const fromInterest = getFirstNonZeroInterest(fromNode);
      const fraction = fromInterest
        ? formatFraction(fromInterest.fractionF)
        : '0.00000000';

      const doc =
        edge.documentId !== null ? documents[String(edge.documentId)] : null;

      const children: LineageStep[] = [];
      if (fromNodeId && !visited.has(fromNodeId)) {
        visited.add(fromNodeId);
        const childSteps = buildLineageSteps(
          fromNodeId,
          graph,
          documents,
          visited,
          depth + 1,
          maxDepth
        );
        children.push(...childSteps);
        visited.delete(fromNodeId);
      }

      consolidationSources.push({
        entityName: fromNode.entity.entityName,
        entityId: fromNode.entity.entityId,
        fraction,
        documentId: edge.documentId ?? null,
        documentTitle: doc?.documentTitle ?? null,
        eventType: getEdgeTypeLabel(edge.eventType, edge.params.retained),
        children,
      });
    }

    const firstSource = consolidationSources[0];

    return [
      {
        fromEntityName: firstSource?.entityName ?? '',
        fromEntityId: firstSource?.entityId ?? 0,
        fromFraction: firstSource?.fraction ?? '0.00000000',
        multiplier: '1.00000000',
        toEntityName: toNode.entity.entityName,
        toFraction,
        documentId: firstSource?.documentId ?? null,
        documentTitle: firstSource?.documentTitle ?? null,
        eventType: firstSource?.eventType ?? '',
        children: [],
        isConsolidation: true,
        consolidationSources,
      },
    ];
  }

  const steps: LineageStep[] = [];

  for (const edgeId of incomingEdgeIds) {
    const edge = graph.edges[edgeId];
    if (!edge) continue;

    const fromNodeId =
      edge.fromNodeId !== null ? String(edge.fromNodeId) : null;
    const toNodeId = edge.toNodeId !== null ? String(edge.toNodeId) : null;

    const fromNode = fromNodeId ? graph.nodes[fromNodeId] : null;
    const toNode = toNodeId ? graph.nodes[toNodeId] : null;

    if (!fromNode || !toNode) continue;

    const toInterest = getFirstNonZeroInterest(toNode);
    const fromInterest = getFirstNonZeroInterest(fromNode);

    const fromFraction = fromInterest
      ? formatFraction(fromInterest.fractionF)
      : '0.00000000';

    const fromF = fromInterest ? fromInterest.fractionF : 0;
    const toF = toInterest ? toInterest.fractionF : 0;
    const ratio = fromF > 0 ? toF / fromF : 1.0;
    const multiplier = formatNumber(ratio, 8, 8, 'decimal', false);
    const toFraction = toInterest
      ? formatFraction(toInterest.fractionF)
      : '0.00000000';

    const doc =
      edge.documentId !== null ? documents[String(edge.documentId)] : null;
    const documentTitle = doc?.documentTitle ?? null;

    const children: LineageStep[] = [];
    if (fromNodeId && !visited.has(fromNodeId)) {
      visited.add(fromNodeId);
      const childSteps = buildLineageSteps(
        fromNodeId,
        graph,
        documents,
        visited,
        depth + 1,
        maxDepth
      );
      children.push(...childSteps);
      visited.delete(fromNodeId);
    }

    const fromNodeIsRoot =
      fromNodeId !== null &&
      (graph.reverseAdjacency[fromNodeId] ?? []).length === 0;

    steps.push({
      fromEntityName: fromNode.entity.entityName,
      fromEntityId: fromNode.entity.entityId,
      fromFraction,
      multiplier,
      toEntityName: toNode.entity.entityName,
      toFraction,
      documentId: edge.documentId ?? null,
      documentTitle,
      eventType: getEdgeTypeLabel(edge.eventType, edge.params.retained),
      children,
      isEstablishmentRoot: fromNodeIsRoot,
    });
  }

  return steps;
}

export function buildLineageTree(
  nodeId: string,
  graph: OwnershipGraph,
  documents: Record<string, DocumentRecord>,
  maxDepth = 8
): LineageStep[] {
  const visited = new Set<string>([nodeId]);
  return buildLineageSteps(nodeId, graph, documents, visited, 0, maxDepth);
}
